import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LABEL = { nicht_ausgelesen: "Vertrag noch nicht ausgelesen", pruefen: "Bitte prüfen", bereit: "Bereit", daten_fehlen: "Daten fehlen", vorhanden: "Laufzettel liegt vor" };

const ansehen = async (id) => {
  try {
    const r = await axios.get(`${API}/teilnehmer/${id}/praktikumsnachweis-pdf`, { responseType: "blob" });
    window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank");
  } catch (e) { toast.error("PDF nicht möglich"); }
};

export const PraktikumsnachweiseBlock = () => {
  const [liste, setListe] = useState(null);
  const [laeuft, setLaeuft] = useState(false);
  const laden = () => axios.get(`${API}/admin/praktikumsnachweise/vorschau`).then((r) => setListe(r.data)).catch(() => toast.error("Vorschau fehlgeschlagen"));
  const bereit = (liste || []).filter((x) => x.status === "bereit");
  const offen = (liste || []).filter((x) => x.status === "nicht_ausgelesen").length;
  const auslesen = async () => {
    setLaeuft(true);
    try {
      for (let i = 0; i < 100; i += 1) {
        const r = await axios.post(`${API}/admin/praktikumsnachweise/auslesen`, {});
        r.data.fehler.forEach((f) => toast.error(`${f.name}: ${f.grund}`));
        if (!r.data.rest || !r.data.ausgelesen.length) break;
        toast.message(`Noch ${r.data.rest} Verträge …`);
      }
      await laden();
    } catch (e) { toast.error("Auslesen fehlgeschlagen"); } finally { setLaeuft(false); }
  };
  const uebernehmen = async (id, quelle) => {
    try { await axios.post(`${API}/admin/praktikumsnachweise/bestaetigen`, { teilnehmer_id: id, quelle }); laden(); }
    catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
  };
  const erstellen = async () => {
    if (!window.confirm(`${bereit.length} Laufzettel erstellen (Teil 1/2 Laufzettel, Teil 2/2 Praktikumsvertrag) und in der Akte unter „Laufzettel“ ablegen?`)) return;
    setLaeuft(true);
    let ok = 0;
    const fehler = [];
    for (const [i, x] of bereit.entries()) {
      toast.message(`Laufzettel ${i + 1} von ${bereit.length} …`, { id: "lz-fortschritt" });
      try {
        const r = await axios.post(`${API}/admin/praktikumsnachweise/erstellen`, { bestaetigt: true, ids: [x.teilnehmer_id] }, { timeout: 120000 });
        ok += r.data.erstellt.length;
        r.data.fehler.forEach((f) => fehler.push(`${f.name}: ${f.grund}`));
      } catch (e) { fehler.push(`${x.name}: ${e.response?.data?.detail || "fehlgeschlagen"}`); }
    }
    toast.success(`${ok} Laufzettel erstellt${fehler.length ? `, ${fehler.length} nicht möglich` : ""}`, { id: "lz-fortschritt" });
    fehler.forEach((f) => toast.error(f));
    setLaeuft(false);
    laden();
  };
  return (
    <div className="border rounded-lg p-4 mb-3" data-testid="praktikumsnachweise-block">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="font-medium">Praktikumsnachweise PHK (1232) Bildungsgutschein ab 2025</p>
          <p className="text-sm text-muted-foreground">Ein PDF je Teilnehmer: Teil 1/2 Laufzettel, Teil 2/2 Praktikumsvertrag.</p>
        </div>
        <Button size="sm" variant="outline" onClick={laden} data-testid="praktikumsnachweise-vorschau">Vorschau</Button>
      </div>
      {liste && (
        <div className="mt-3 text-sm">
          <p className="text-muted-foreground mb-2" data-testid="praktikumsnachweise-zaehler">
            {Object.keys(LABEL).map((s) => `${liste.filter((x) => x.status === s).length} ${LABEL[s]}`).join(" · ")}
          </p>
          <ul className="max-h-72 overflow-y-auto divide-y">
            {liste.map((x) => (
              <li key={x.teilnehmer_id} className="py-1.5 flex items-center justify-between gap-2" data-testid={`praktikumsnachweis-${x.teilnehmer_id}`}>
                <span>{x.name || "(ohne Name)"} · {x.betrieb || "–"} · <span className="text-muted-foreground">{LABEL[x.status]}{x.fehlt.length ? `: ${x.fehlt.join(", ")}` : ""}</span></span>
                {x.status === "bereit" && <button className="text-xs underline" onClick={() => ansehen(x.teilnehmer_id)} data-testid={`praktikumsnachweis-ansehen-${x.teilnehmer_id}`}>Ansehen</button>}
                {x.status === "pruefen" && (
                  <span className="flex flex-col items-end gap-1 text-xs" data-testid={`praktikumsnachweis-pruefen-${x.teilnehmer_id}`}>
                    {x.abweichungen.map((a) => <span key={a.feld}>{a.feld}: Akte „{a.akte || "leer"}“ · Vertrag „{a.vertrag}“</span>)}
                    {x.vertrag && !x.vertrag.sicher && <span className="text-[#B4502F]">Vertrag schwer lesbar – bitte Scan ansehen</span>}
                    <span className="flex gap-2">
                      <button className="underline" onClick={() => uebernehmen(x.teilnehmer_id, "vertrag")} data-testid={`praktikumsnachweis-vertrag-${x.teilnehmer_id}`}>Vertrag übernehmen</button>
                      {x.abweichungen.every((a) => a.akte) && <button className="underline" onClick={() => uebernehmen(x.teilnehmer_id, "akte")} data-testid={`praktikumsnachweis-akte-${x.teilnehmer_id}`}>Akte behalten</button>}
                    </span>
                  </span>
                )}
              </li>
            ))}
          </ul>
          {offen > 0 && <Button size="sm" variant="outline" className="mt-3 mr-2" disabled={laeuft} onClick={auslesen} data-testid="praktikumsnachweise-auslesen">{laeuft ? "Liest Verträge …" : `${offen} Verträge auslesen`}</Button>}
          {bereit.length > 0 && <Button size="sm" className="mt-3" disabled={laeuft} onClick={erstellen} data-testid="praktikumsnachweise-erstellen">{bereit.length} Laufzettel erstellen</Button>}
        </div>
      )}
    </div>
  );
};
